import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
    deleteFundingStatus,
    FundingStatusItem,
    getFundingStatusHistory,
} from "../../../services/api/fundingStatus";
import { AuthContext } from "../../../contexts/AuthContext";
import Loader from "../../../components/Loader";
import Text from "../../../components/Text";
import IconButton from "../../../components/IconButton";
import ConfirmModal from "../../../components/ConfirmModal";
import { DeleteV2Icon } from "../../../assets/img/icons";
import { MASKED_PERFORMANCE_VALUE } from "../../../utils/tradesPerfKpisCookie";
import styles from "./styles.module.scss";

interface Props {
    userId: string;
    hidePerformance: boolean;
}

type Evolution = "up" | "down" | "same" | null;

function formatAccountsDelta(diff: number): string {
    const abs = Math.abs(diff);
    const noun = abs === 1 ? "compte" : "comptes";
    if (diff > 0) return `Ajout de ${abs} ${noun}`;
    return `Perte de ${abs} ${noun}`;
}

function formatCapitalDelta(
    diff: number,
    formatBalance: (value: number) => string
): string {
    const formatted = formatBalance(Math.abs(diff));
    if (diff > 0) return `Capital +${formatted} $`;
    return `Capital -${formatted} $`;
}

/** Libellé colonne Évolution : comptes et/ou capital, sans « 0 comptes ». */
export function buildFundingEvolutionLabel(
    accountsDiff: number | null,
    balanceDiff: number | null,
    formatBalance: (value: number) => string
): string {
    if (accountsDiff === null && balanceDiff === null) {
        return "";
    }

    const parts: string[] = [];
    if (accountsDiff != null && accountsDiff !== 0) {
        parts.push(formatAccountsDelta(accountsDiff));
    }
    if (balanceDiff != null && balanceDiff !== 0) {
        parts.push(formatCapitalDelta(balanceDiff, formatBalance));
    }

    return parts.length > 0 ? parts.join(" · ") : "Stable";
}

const FundingHistory: FC<Props> = ({
    userId,
    hidePerformance,
}) => {
    const { hasPermission } = useContext(AuthContext)!;
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState<FundingStatusItem[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [confirmModal, setConfirmModal] = useState<{
        open: boolean;
        item: FundingStatusItem | null;
        isLoading: boolean;
    }>({ open: false, item: null, isLoading: false });

    const canDelete = hasPermission("admin-funding-status", "delete");

    const loadHistory = useCallback(() => {
        setLoading(true);
        setError(null);
        getFundingStatusHistory(userId, { sort: "asOfDate:desc" })
            .then((res) => setItems(res?.items ?? []))
            .catch(() => setError("Impossible de charger l'historique"))
            .finally(() => setLoading(false));
    }, [userId]);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    const handleConfirmOpen = (item: FundingStatusItem) => {
        setConfirmModal({ open: true, item, isLoading: false });
    };

    const handleConfirmClose = () => {
        setConfirmModal({ open: false, item: null, isLoading: false });
    };

    const handleConfirmDelete = async () => {
        if (!confirmModal.item) return;
        try {
            setConfirmModal((prev) => ({ ...prev, isLoading: true }));
            await deleteFundingStatus(userId, confirmModal.item.id);
            toast.success("Historique de financement supprimé");
            loadHistory();
        } catch {
            toast.error("Une erreur s'est produite, veuillez réessayer plus tard");
        } finally {
            handleConfirmClose();
        }
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    };

    const formatBalance = (value: string | number) => {
        const n = typeof value === "string" ? Number(value) : value;
        return new Intl.NumberFormat("fr-FR", {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
        }).format(n);
    };
    const displayValue = (value: string | number) =>
        hidePerformance ? MASKED_PERFORMANCE_VALUE : value;

    /** Pour chaque entrée, calcule l'évolution par rapport au snapshot précédent (dans le temps). */
    const rowsWithEvolution = useMemo(() => {
        const sorted = [...items].sort(
            (a, b) => new Date(b.asOfDate).getTime() - new Date(a.asOfDate).getTime()
        );
        return sorted.map((item, index) => {
            const prev = sorted[index + 1];
            let accountsEvolution: Evolution = null;
            let balanceEvolution: Evolution = null;
            let accountsDiff: number | null = null;
            let balanceDiff: number | null = null;

            if (prev) {
                const accCur = item.fundedAccountsCount ?? 0;
                const accPrev = prev.fundedAccountsCount ?? 0;
                const balCur = Number(item.fundedTotalBalance || "0");
                const balPrev = Number(prev.fundedTotalBalance || "0");

                accountsDiff = accCur - accPrev;
                balanceDiff = balCur - balPrev;
                accountsEvolution =
                    accountsDiff > 0 ? "up" : accountsDiff < 0 ? "down" : "same";
                balanceEvolution =
                    balanceDiff > 0 ? "up" : balanceDiff < 0 ? "down" : "same";
            }

            return {
                item,
                accountsEvolution,
                balanceEvolution,
                accountsDiff,
                balanceDiff,
            };
        });
    }, [items]);

    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.tableau}>
                    <h2 className={styles.title}>Historique de financement</h2>
                    <div className={styles.titleSeparator} />
                    <div className={styles.loaderWrap}>
                        <Loader />
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.tableau}>
                    <h2 className={styles.title}>Historique de financement</h2>
                    <div className={styles.titleSeparator} />
                    <div className={styles.noDataWrap}>
                        <Text variant="subtitle" className={styles.errorText}>
                            {error}
                        </Text>
                    </div>
                </div>
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className={styles.container}>
                <div className={styles.tableau}>
                    <h2 className={styles.title}>Historique de financement</h2>
                    <div className={styles.titleSeparator} />
                    <div className={styles.noDataWrap}>
                        <Text variant="subtitle">
                            Aucun statut de financement enregistré
                        </Text>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <ConfirmModal
                open={confirmModal.open}
                handleClose={handleConfirmClose}
                handleConfirm={handleConfirmDelete}
                isLoading={confirmModal.isLoading}
            />
            <div className={styles.tableau}>
                <h2 className={styles.title}>Historique de financement</h2>
                <div className={styles.titleSeparator} />
                <div className={`${styles.scrollWrap} table-wrapper`}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th className={styles.thDate}>
                                    Date de mise à jour
                                </th>
                                <th className={styles.thNumber}>
                                    Nb de comptes
                                </th>
                                <th className={styles.thBalance}>
                                    Capital total (capital du compte + PnL)
                                </th>
                                <th className={styles.thEvolution}>
                                    Évolution
                                </th>
                                {canDelete && (
                                    <th className={styles.thActions} />
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {rowsWithEvolution.map(({ item, accountsEvolution, balanceEvolution, accountsDiff, balanceDiff }) => (
                                <tr key={item.id} className={styles.tr}>
                                    <td className={styles.tdDate}>
                                        {formatDate(item.asOfDate)}
                                    </td>
                                    <td className={styles.tdNumber}>
                                        <span className={styles.value}>
                                            {displayValue(
                                                item.fundedAccountsCount ?? 0
                                            )}
                                        </span>
                                        {!hidePerformance &&
                                            accountsEvolution !== null && (
                                            <span
                                                className={
                                                    accountsEvolution === "up"
                                                        ? styles.evolUp
                                                        : accountsEvolution === "down"
                                                        ? styles.evolDown
                                                        : styles.evolSame
                                                }
                                            >
                                                {accountsEvolution === "up" && " ↑ "}
                                                {accountsEvolution === "down" && " ↓ "}
                                                {accountsDiff !== null &&
                                                    accountsDiff !== 0 && (
                                                        <span className={styles.diff}>
                                                            {accountsDiff > 0
                                                                ? `+${accountsDiff}`
                                                                : accountsDiff}
                                                        </span>
                                                    )}
                                            </span>
                                        )}
                                    </td>
                                    <td className={styles.tdBalance}>
                                        <span className={styles.value}>
                                            {displayValue(
                                                `${formatBalance(
                                                    item.fundedTotalBalance ||
                                                        "0"
                                                )} $`
                                            )}
                                        </span>
                                        {!hidePerformance &&
                                            balanceEvolution !== null && (
                                            <>
                                            <br />
                                            <span
                                                className={
                                                    balanceEvolution === "up"
                                                        ? styles.evolUp
                                                        : balanceEvolution === "down"
                                                        ? styles.evolDown
                                                        : styles.evolSame
                                                }
                                            >
                                                {balanceEvolution === "up" && " ↑ "}
                                                {balanceEvolution === "down" && " ↓ "}
                                                {balanceDiff !== null &&
                                                    balanceDiff !== 0 && (
                                                        <span className={styles.diff}>
                                                            {balanceDiff > 0
                                                                ? `+${formatBalance(balanceDiff)}`
                                                                : formatBalance(balanceDiff)}{" "}
                                                            $
                                                        </span>
                                                    )}
                                            </span>
                                            </>
                                        )}
                                    </td>
                                    <td className={styles.tdEvolution}>
                                        {hidePerformance ? (
                                            <span className={styles.evolNone}>
                                                {MASKED_PERFORMANCE_VALUE}
                                            </span>
                                        ) : accountsEvolution === null &&
                                        balanceEvolution === null ? (
                                            <span className={styles.evolNone}>
                                                {item.isFunded
                                                    ? "Premier financement"
                                                    : "Déclaration initiale"}
                                            </span>
                                        ) : (
                                            <span
                                                className={
                                                    (accountsDiff ?? 0) > 0 ||
                                                    (balanceDiff ?? 0) > 0
                                                        ? styles.evolUp
                                                        : (accountsDiff ?? 0) < 0 ||
                                                            (balanceDiff ?? 0) < 0
                                                          ? styles.evolDown
                                                          : styles.evolSame
                                                }
                                            >
                                                {buildFundingEvolutionLabel(
                                                    accountsDiff,
                                                    balanceDiff,
                                                    formatBalance
                                                )}
                                            </span>
                                        )}
                                    </td>
                                    {canDelete && (
                                        <td className={styles.tdActions}>
                                            <IconButton
                                                icon={DeleteV2Icon}
                                                onClick={() => handleConfirmOpen(item)}
                                                size="medium"
                                                color="secondary"
                                            />
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default FundingHistory;
